import React from "react";
import type { FC, PropsWithChildren } from "react";
import { inputPhoneMasking, unMasking } from "../../utils/validators";
import {
  InputsSection,
  InputBox,
  ModalButtons,
  ModalForm,
  Modal,
  SelectBox,
  TextArea,
  Button,
} from "../../../src/components/Modal";
import { TableButtons } from "../../components/Table";
import ModalHeading from "../../../src/components/Modal/ModalHeading";
import AddUserSkeleton from "../../../src/Skeletons/AddUserSkeleton";
import AssociatedClinicSkeleton from "../../Skeletons/AssociatedClinicSkeleton";
import DisplayBox from "../../components/Modal/DisplayBox";
import {
  IAssociateClinic,
  IAssociateClinicNew,
  IFields,
} from "../../hooks/associateClinic/index.type";

export type AddEditModalState = PropsWithChildren<{
  status: boolean;
  data?: IAssociateClinic;
}>;

export type IaddEditModalStates = PropsWithChildren<{
  name: { value: string; error: string };
  contactName: { value: string; error: string };
  email: { value: string; error: string };
  phone: { value: string; error: string };
  billingProvider: { value: string[]; error: string };
  address: { value: string; error: string };
}>;

type AddEditModalProps = PropsWithChildren<{
  loading: boolean;
  addEditModal: AddEditModalState;
  onCloseModal?: () => void;
  handleAddEdit?: (event: React.FormEvent<HTMLFormElement>) => void;
  addEditModalStates: IaddEditModalStates;
  setAddEditModalStates: React.Dispatch<
    React.SetStateAction<IaddEditModalStates>
  >;
  clinicTypes?: IFields[];
}>;

const AddEditModal: FC<AddEditModalProps> = (props) => {
  const [providerInput, setProviderInput] = React.useState("");
  const [editingProvider, setEditingProvider] = React.useState<string | null>(null);
  const [editProviderValue, setEditProviderValue] = React.useState("");
  const originalProvidersRef = React.useRef<string[]>([]);

React.useEffect(() => {
  if (props.addEditModal.data?.billingProvider) {
    originalProvidersRef.current = props.addEditModal.data.billingProvider
      .map((provider) =>
        typeof provider === "string" ? provider : provider.name || ""
      )
      .filter((name) => name !== "");
  } else {
    originalProvidersRef.current = [];
  }
}, [props.addEditModal.data]);


  const handleDeleteProvider = (providerToDelete: string) => {
    props.setAddEditModalStates((prev) => ({
      ...prev,
      billingProvider: {
        value: prev.billingProvider.value.filter(
          (provider) => provider !== providerToDelete
        ),
        error: "",
      },
    }));
  };

  const handleAddProvider = () => {
    const trimmed = providerInput.trim();

    if (!trimmed) {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        billingProvider: {
          ...prev.billingProvider,
          error: "Provider name cannot be empty",
        },
      }));
      return;
    }

    if (!props.addEditModalStates.billingProvider.value.includes(trimmed)) {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        billingProvider: {
          value: [...prev.billingProvider.value, trimmed],
          error: "",
        },
      }));
      setProviderInput("");
    }
  };

  const handleEditProvider = (provider: string) => {
    setEditingProvider(provider);
    setEditProviderValue(provider);
  };

  const handleSaveEdit = () => {
    const trimmed = editProviderValue.trim();
    
    if (!trimmed) {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        billingProvider: {
          ...prev.billingProvider,
          error: "Provider name cannot be empty",
        },
      }));
      return;
    }

    if (trimmed !== editingProvider && props.addEditModalStates.billingProvider.value.includes(trimmed)) {
      props.setAddEditModalStates((prev) => ({
        ...prev,
        billingProvider: {
          ...prev.billingProvider,
          error: "Provider name already exists",
        },
      }));
      return;
    }

    props.setAddEditModalStates((prev) => ({
      ...prev,
      billingProvider: {
        value: prev.billingProvider.value.map(provider => 
          provider === editingProvider ? trimmed : provider
        ),
        error: "",
      },
    }));
    
    setEditingProvider(null);
    setEditProviderValue("");
  };

  const handleCancelEdit = () => {
    setEditingProvider(null);
    setEditProviderValue("");
  };

  return (
    <Modal
      show={props.addEditModal.status}
      style={{ height: "65rem", overflowY: "auto" }}
    >
      <ModalHeading
        heading={
          props.addEditModal.data && props.addEditModal.data._id
            ? "Update Associated Clinic"
            : "Add Associated Clinic"
        }
      />
      {props.loading ? (
        <AssociatedClinicSkeleton />
      ) : (
        <ModalForm
          style={{ marginBottom: "2.5rem" }}
          onSubmit={props.handleAddEdit}
        >
          <InputsSection>
            <InputBox
              label="Name"
              placeholder="eg., John Clinic"
              value={props.addEditModalStates.name.value}
              onChange={(event) => {
                props.setAddEditModalStates((prevVal) => ({
                  ...prevVal,
                  name: { value: event.target.value, error: "" },
                }));
              }}
              errorMessage={props.addEditModalStates.name.error}
            />
            <InputBox
              label="Contact Email"
              placeholder="eg, johndoe@example.com"
              value={props.addEditModalStates.email.value}
              onChange={(event) => {
                props.setAddEditModalStates((prevVal) => ({
                  ...prevVal,
                  email: { value: event.target.value, error: "" },
                }));
              }}
              errorMessage={props.addEditModalStates.email.error}
              disabled={
                props.addEditModal.data && props.addEditModal.data._id
                  ? true
                  : false
              }
            />
            <InputBox
              label="Contact Phone"
              placeholder="(113)-882-1638"
              value={inputPhoneMasking(props.addEditModalStates.phone.value)}
              onChange={(event) => {
                const maskedValue = inputPhoneMasking(event.target.value);
                props.setAddEditModalStates((prevVal) => ({
                  ...prevVal,
                  phone: { 
                    value: unMasking(maskedValue), 
                    error: "" 
                  },
                }));
              }}
              errorMessage={props.addEditModalStates.phone.error}
              disabled={
                props.addEditModal.data && props.addEditModal.data._id
                  ? true
                  : false
              }
            />
          </InputsSection>

          <InputsSection
            style={{
              display: "flex",
              gap: "5rem",
              height: "20rem",
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <InputBox
                label="Providers Name"
                placeholder="eg. John Doe, Jane Smith"
                value={providerInput}
                onChange={(e) => setProviderInput(e.target.value)}
                errorMessage={props.addEditModalStates.billingProvider.error}
              />
              <Button
                label="Add"
                span={false}
                onClick={handleAddProvider}
                submitButtonStyle={{ height: "4rem", marginTop: "2.5rem" }}
                type="button"
              />
            </div>

            <div
              style={{
                marginTop: "2.5rem",
                display: "grid",
                gridTemplateColumns: "2fr 2fr",
                columnGap: "1rem",
              }}
            >
              {props.addEditModalStates.billingProvider.value.map(
                (provider, index) => {
                  const isNewProvider = !originalProvidersRef.current.includes(
                    provider
                  );

                  const showTrashIcon =
                    !props.addEditModal.data?._id || isNewProvider;

                  const isEditing = editingProvider === provider;

                  return (
                    <span
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      {isEditing ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <InputBox
                            value={editProviderValue}
                            onChange={(e) => setEditProviderValue(e.target.value)}
                            placeholder="Provider name"
                            style={{
                              width: "12rem",
                              fontSize: "1.4rem",
                            }}
                          />
                          <TableButtons
                            buttons={[
                              {
                                icon: "check",
                                tooltip: "Save",
                                variant: "normal",
                                onClick: handleSaveEdit,
                              },
                              {
                                icon: "x",
                                tooltip: "Cancel",
                                variant: "normal",
                                onClick: handleCancelEdit,
                              },
                            ]}
                          />
                        </div>
                      ) : (
                        <>
                          <DisplayBox
                            text={provider}
                            style={{
                              fontSize: "0.5rem",
                              width: "12rem",
                              borderRadius: "0.6rem",
                              backgroundColor: "#F2F8FF",
                              paddingLeft: "0.5rem",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                              padding: "0.5rem",
                              display: "flex",
                              flexDirection: "row-reverse",
                            }}
                          />
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <TableButtons
                              buttons={[
                                {
                                  icon: "edit-3",
                                  tooltip: "Edit",
                                  variant: "normal",
                                  onClick: () => handleEditProvider(provider),
                                },
                              ]}
                            />
                            {showTrashIcon && (
                              <TableButtons
                                buttons={[
                                  {
                                    icon: "trash",
                                    tooltip: "Delete",
                                    variant: "danger",
                                    onClick: () => handleDeleteProvider(provider),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </span>
                  );
                }
              )}
            </div>
          </InputsSection>

          <InputsSection>
            <TextArea
              label="Address"
              placeholder="Address"
              value={props.addEditModalStates.address.value}
              onChange={(event) => {
                props.setAddEditModalStates((prevVal) => ({
                  ...prevVal,
                  address: { value: event.target.value, error: "" },
                }));
              }}
              errorMessage={props.addEditModalStates.address.error}
              style={{ width: "79rem" }}
            />
          </InputsSection>

          <ModalButtons
            submitButtonLabel={
              props.addEditModal.data && props.addEditModal.data._id
                ? "Update"
                : "Create"
            }
            cancelButtonLabel="Cancel"
            onCancel={props.onCloseModal}
          />
        </ModalForm>
      )}
    </Modal>
  );
};

export default AddEditModal;
